/**
 * Runtime-loaded voxel cosmetic asset shape.
 *
 * Produced by `packages/visual-engine/scripts/vox-bake.mjs` from a
 * MagicaVoxel `.vox` source file. Loaded by `<VoxelDisplay>` (W2) and
 * `<VoxelAnimation>` (W3 — animations carry a wrapping manifest that
 * points at per-frame `BakedVoxelAsset` JSONs).
 *
 * Schema versioned (`schema_version: "1.0.0"`) so the loader can
 * detect a backward-incompatible bake-output bump and bail loudly
 * rather than rendering nonsense.
 *
 * Plan reference: docs/dev/visual-engine/voxel-cosmetics-plan.md §3.
 */

import type { MaterialSlot, Voxel } from "../voxel/types";

/** The shape emitted by `vox:bake`. */
export interface BakedVoxelAsset {
  /** Stable id (typically the SKU id). */
  id: string;
  /** Bake-schema version. Loader rejects mismatches with a clear error. */
  schema_version: string;
  /** Origin offset relative to part-local 0 (tune via Anchor Tuner). */
  anchor: { x: number; y: number; z: number };
  /** Pre-computed engine-space bounding box for camera-fit + layout. */
  bbox: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
    size: { x: number; y: number; z: number };
  };
  /** Default palette baked in alongside the model. Consumer can override. */
  slot_palette: Record<MaterialSlot, string>;
  /** The voxel grid. */
  voxels: ReadonlyArray<Voxel>;
}

/** The wrapping manifest emitted by `vox:bake-animation` (W3). */
export interface BakedVoxelAnimation {
  id: string;
  schema_version: string;
  fps: number;
  loop: boolean;
  /** Relative paths to per-frame `BakedVoxelAsset` JSONs. */
  frames: string[];
  /** Pass-through from the authored manifest.yaml; runtime interprets. */
  seeded_variations?: unknown;
}

export const BAKE_SCHEMA_VERSION = "1.0.0" as const;
